import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import StepIndicator from '../components/StepIndicator';
import { formatDate } from '../data/dateLocale';
import { User, Mail, ChevronRight, ChevronDown } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+372', country: 'EE' },
  { code: '+371', country: 'LV' },
  { code: '+370', country: 'LT' },
  { code: '+48', country: 'PL' },
  { code: '+7', country: 'RU' },
  { code: '+358', country: 'FI' },
  { code: '+49', country: 'DE' },
  { code: '+44', country: 'GB' },
  { code: '+33', country: 'FR' },
  { code: '+39', country: 'IT' },
  { code: '+34', country: 'ES' },
  { code: '+46', country: 'SE' },
  { code: '+47', country: 'NO' },
  { code: '+45', country: 'DK' },
  { code: '+375', country: 'BY' },
  { code: '+380', country: 'UA' },
];

const FlagImg = ({ country, size = 20 }) => (
  <img
    src={`https://flagcdn.com/w40/${country.toLowerCase()}.png`}
    alt={country}
    width={size}
    height={Math.round(size * 0.75)}
    className="inline-block rounded-sm object-cover"
    style={{ minWidth: size }}
  />
);

const PassengerDetailsPage = () => {
  const navigate = useNavigate();
  const { selectedTrip, selectedReturnTrip, searchParams, passengerInfo, setPassengerInfo, setCurrentStep, totalPrice } = useBooking();
  const { t, lang } = useLanguage();
  const [phoneCode, setPhoneCode] = useState(COUNTRY_CODES[0]);
  const [phoneCodeOpen, setPhoneCodeOpen] = useState(false);
  const phoneCodeRef = useRef(null);

  useEffect(() => { setCurrentStep(3); }, [setCurrentStep]);

  useEffect(() => {
    const handler = (e) => {
      if (phoneCodeRef.current && !phoneCodeRef.current.contains(e.target)) setPhoneCodeOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  useEffect(() => {
    if (!selectedTrip) navigate('/');
  }, [selectedTrip, navigate]);

  if (!selectedTrip) return null;

  const handleChange = (field, value) => {
    setPassengerInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!passengerInfo.firstName || !passengerInfo.lastName || !passengerInfo.email || !passengerInfo.phone) return;
    setCurrentStep(4);
    navigate('/payment');
  };

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <StepIndicator />
      <div className="max-w-[1100px] mx-auto px-3 md:px-4 py-6 md:py-8">
        <h1 className="text-xl font-bold mb-6">{t('passenger.title')}</h1>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#777] mb-6">{t('passenger.label')}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('passenger.firstName')} *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                    <input type="text" required value={passengerInfo.firstName} onChange={(e) => handleChange('firstName', e.target.value)} placeholder={t('passenger.firstNamePlaceholder')} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('passenger.lastName')} *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                    <input type="text" required value={passengerInfo.lastName} onChange={(e) => handleChange('lastName', e.target.value)} placeholder={t('passenger.lastNamePlaceholder')} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div>
                  <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('passenger.email')} *</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                    <input type="email" required value={passengerInfo.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="email@example.com" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors h-[48px]" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('passenger.phone')} *</label>
                  <div className="flex">
                    <div className="relative" ref={phoneCodeRef}>
                      <button
                        type="button"
                        onClick={() => setPhoneCodeOpen(!phoneCodeOpen)}
                        className="flex items-center gap-1.5 px-3 border border-r-0 border-gray-200 rounded-l-lg text-sm hover:bg-gray-50 transition-colors bg-white h-[48px]"
                      >
                        <FlagImg country={phoneCode.country} />
                        <span className="font-medium text-[#484c55] text-sm">{phoneCode.code}</span>
                        <ChevronDown size={12} className="text-[#484c55]/40" />
                      </button>
                      {phoneCodeOpen && (
                        <div className="absolute top-full left-0 bg-white rounded-lg shadow-xl border mt-1 z-50 max-h-[240px] overflow-auto w-[180px]">
                          {COUNTRY_CODES.map((cc) => (
                            <div
                              key={cc.code}
                              className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-[#f0f0f1] transition-colors ${phoneCode.code === cc.code ? 'bg-[#f0f0f1]' : ''}`}
                              onClick={() => { setPhoneCode(cc); setPhoneCodeOpen(false); }}
                            >
                              <FlagImg country={cc.country} />
                              <span className="text-sm font-medium">{cc.country}</span>
                              <span className="text-sm text-[#555] ml-auto">{cc.code}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      required
                      value={passengerInfo.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder={t('passenger.phonePlaceholder')}
                      className="flex-1 px-4 border border-gray-200 rounded-r-lg text-sm outline-none focus:border-[#c30909] transition-colors h-[48px]"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <button type="button" onClick={() => navigate('/seats')} className="text-sm text-[#555] hover:text-[#484c55] transition-colors">
                  {t('passenger.backToSeats')}
                </button>
                <button type="submit" className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-sm uppercase tracking-wider px-8 py-3 rounded-lg transition-colors flex items-center gap-2">
                  {t('passenger.toPayment')}
                  <ChevronRight size={16} />
                </button>
              </div>
            </form>
          </div>

          <div className="w-full md:w-[340px] flex-shrink-0">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-4">
              <h2 className="text-lg font-bold mb-4">{t('summary.title')}</h2>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#c30909]" />
                  <span className="text-xs font-semibold text-[#777] uppercase">{t('summary.outbound')}</span>
                </div>
                <p className="text-sm font-bold">{formatDate(searchParams.departureDate, lang)}</p>
                <p className="text-xs text-[#555] mt-1">{selectedTrip.departureTime} {selectedTrip.from} — {selectedTrip.arrivalTime} {selectedTrip.to}</p>
              </div>
              {selectedReturnTrip && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#484c55]/30" />
                    <span className="text-xs font-semibold text-[#777] uppercase">{t('summary.return')}</span>
                  </div>
                  <p className="text-sm font-bold">{formatDate(searchParams.returnDate, lang)}</p>
                  <p className="text-xs text-[#555] mt-1">{selectedReturnTrip.departureTime} {selectedReturnTrip.from} — {selectedReturnTrip.arrivalTime} {selectedReturnTrip.to}</p>
                </div>
              )}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#555]">{t('summary.passenger')} × {Object.values(searchParams.passengers).reduce((a, b) => a + b, 0)}</span>
                  <span className="text-sm font-medium">{totalPrice()} €</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-base font-bold">{t('summary.total')}</span>
                  <span className="text-xl font-bold">{totalPrice()} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassengerDetailsPage;
